/**
 * get_job Tool - Quick job status check without polling
 *
 * Get current status of a job without waiting. Useful for checking progress.
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import type { ActiveJobStatus } from '@runhuman/shared';
import { isTerminalStatus } from '@runhuman/shared';
import type { JobClient } from '../clients/job-client.js';
import { formatTesterPool } from '../utils/format-tester-pool.js';
import { formatCompletedJob, formatTerminalFailure } from '../utils/format-job-result.js';
import { OUTCOME_BUCKETS_GUIDE } from '../utils/outcome-guide.js';

export interface GetJobArgs {
  jobId: string;
}

export const getJobToolDefinition: Tool = {
  name: 'get_job',
  description: `Get the current status of a QA test job without waiting.

Returns immediate snapshot of job state including status, modifier (granular reason), passStatus (AI Pass/Fail/Not-Applicable verdict), results, tester info, cost, and duration.

Active statuses (job still in flight):
- pending: Waiting for tester to claim
- preparing: System preparing the test
- queued: Waiting in the tester's job queue
- waiting: Tester claimed but hasn't started
- working: Tester is actively testing
- overdue: Tester missed the deadline but may still submit a late result; can also resolve to expired if they drop or the late-submission window lapses

Terminal statuses (job finished, no further change):
- completed: Test finished. The tri-state \`passStatus\` field then carries the AI verdict (pass / fail / not_applicable).
- cancelled: Cancelled before completion. \`modifier\` explains who/why (tester_cancellation, admin_terminated, tester_invalidated).
- expired: Tester dropped, was unresponsive, or no tester was available. See \`modifier\`.
- failed: Platform or AI error. See \`modifier\` (platform_issue, system_error, ai_extraction_failed, ai_extraction_incomplete, unreachable_url, safety_check_failed).

${OUTCOME_BUCKETS_GUIDE}

For automatic polling until completion, use the wait tool instead.`,
  inputSchema: {
    type: 'object',
    properties: {
      jobId: {
        type: 'string',
        description: 'Job ID to check (from create_job or run_template)',
      },
    },
    required: ['jobId'],
  },
};

const ACTIVE_STATUS_DESCRIPTIONS: Record<ActiveJobStatus, string> = {
  pending: 'Waiting for tester to claim',
  preparing: 'System preparing the test',
  queued: 'Waiting in the tester\'s job queue',
  waiting: 'Tester claimed but has not started yet',
  working: 'Tester is actively testing',
  overdue: 'Tester missed the deadline — they may still submit a late result, or the job may resolve to expired if they drop or the late-submission window lapses',
  admin_hold: 'Admin paused the job to preserve tester work — awaiting an admin decision to terminate or reprocess the saved work to completion',
};

export async function handleGetJob(
  args: GetJobArgs,
  jobClient: JobClient
): Promise<{ content: Array<{ type: 'text'; text: string }>; isError?: boolean }> {
  const { jobId } = args;

  try {
    const job = await jobClient.getJobStatus(jobId);

    if (isTerminalStatus(job.status)) {
      if (job.status === 'completed') {
        return {
          content: [{ type: 'text', text: formatCompletedJob(jobId, job) }],
        };
      }

      return {
        content: [{ type: 'text', text: formatTerminalFailure(jobId, job) }],
        isError: true,
      };
    }

    const description = ACTIVE_STATUS_DESCRIPTIONS[job.status as ActiveJobStatus];
    const testerPoolInfo = formatTesterPool(job);

    return {
      content: [{
        type: 'text',
        text: `Job In Progress (${job.status})

Job ID: ${jobId}
Status: ${job.status}${testerPoolInfo}

Current state:
- ${description}

Next steps:
- Use wait to poll until completion: wait(jobId: "${jobId}")
- Or call get_job again to check progress
${job.jobUrl ? `- View in dashboard: ${job.jobUrl}` : ''}`,
      }],
    };
  } catch (error) {
    const err = error as Error & { status?: number };

    if (err.message?.includes('not found')) {
      return {
        content: [{
          type: 'text',
          text: `Job Not Found

Job ID: ${jobId}

The job "${jobId}" does not exist or you don't have access to it.

Please check:
- Job ID is correct
- Job exists in your project
- Your PAT has access to this job (create one at https://runhuman.com/dashboard/settings/tokens)`,
        }],
        isError: true,
      };
    }

    return {
      content: [{
        type: 'text',
        text: `Failed to get job status

Job ID: ${jobId}
Error: ${err.message}
${err.status ? `Status: ${err.status}` : ''}`,
      }],
      isError: true,
    };
  }
}
